import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { useAuth } from '@/hooks/useAuth';
import { DataModelAttribute } from '@/types/DataModel';

import { useAttributesStateStore } from './useAttributesStore';

/**
 * When true, attributes are always considered editable (e.g. in Mass Update flow).
 * Used to bypass permission/readonly checks in that context.
 */
const ForceAttributeEditableContext = createContext<boolean>(false);

const ForceAttributeEditableProvider = ({ children, value }: PropsWithChildren<{ value: boolean }>) => (
    <ForceAttributeEditableContext.Provider value={value}>{children}</ForceAttributeEditableContext.Provider>
);

type EditPermissionCheck = (values: Record<string, unknown>) => boolean;

/**
 * Replaces the item edit-permission check for editors of other records (e.g. assets), which
 * `auth.hasItemPermission` does not cover. Unlike {@link ForceAttributeEditableProvider}, read-only
 * states and the read-only configuration of attributes still apply.
 */
const AttributeEditPermissionContext = createContext<EditPermissionCheck | null>(null);

const AttributeEditPermissionProvider = ({ children, canEdit }: PropsWithChildren<{ canEdit: EditPermissionCheck }>) => (
    <AttributeEditPermissionContext.Provider value={canEdit}>{children}</AttributeEditPermissionContext.Provider>
);

/**
 * Determines if an attribute should be readonly based on multiple conditions:
 * - User permissions
 * - Parent component readonly state
 * - Attribute state from store
 * - Attribute configuration (readonly flag or additionalParams.readonly)
 *
 * @param attribute - The data model attribute to check
 * @param fieldPath - The field path used to look up readonly state in the store
 * @param isReadonlyByParent - Whether the parent component has marked this as readonly
 * @returns true if the attribute should be readonly, false otherwise
 */
const useIsAttributeReadonly = (
    attribute: DataModelAttribute,
    fieldPath: string,
    isReadonlyByParent: boolean
): boolean => {
    const forceEditable = useContext(ForceAttributeEditableContext);
    const canEdit = useContext(AttributeEditPermissionContext);
    const { auth } = useAuth();
    const { getValues } = useFormContext();

    const isReadonlyByAttributeState = useAttributesStateStore(
        useShallow((state) => state.readonly[fieldPath])
    );

    const isReadonlyByAdditionalParams = Boolean(
        attribute.readonly || attribute.additionalParams?.readonly
    );

    return useMemo(() => {
        // Mass Update (and similar flows) can force attributes to be editable
        if (forceEditable) {
            return false;
        }
        if (canEdit) {
            if (!canEdit(getValues())) {
                return true;
            }
        } else if (auth) {
            // Only check permissions if auth is available
            // If auth is not yet loaded, we'll check other conditions and assume permission is granted
            const hasPermission = auth.hasItemPermission('edit', getValues());
            // If user doesn't have edit permission, attribute is readonly
            if (!hasPermission) {
                return true;
            }
        }

        // Attribute is readonly if any of these conditions are true:
        // - Parent component marked it as readonly
        // - Attribute state store has it marked as readonly
        // - Attribute configuration has readonly flag set
        return (
            isReadonlyByParent ||
            isReadonlyByAttributeState ||
            isReadonlyByAdditionalParams
        );
    }, [
        forceEditable,
        canEdit,
        auth,
        getValues,
        isReadonlyByParent,
        isReadonlyByAttributeState,
        isReadonlyByAdditionalParams,
    ]);
};

export { AttributeEditPermissionProvider, ForceAttributeEditableProvider, useIsAttributeReadonly };
